'use client';

import { ArrowDown, ChevronDown } from 'lucide-react';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';

/**
 * Stone Investment Hero Variants
 * 
 * Concept One Design (EXACT MATCH) - Three hero styles:
 * 
 * 1. CINEMATIC - Home page hero with video background
 * 2. EDITORIAL - The Firm page hero with pulse animation (full viewport)
 * 3. ABSTRACT - Valuations page hero with gradient fade
 */

// =============================================================================
// SHARED TYPES
// =============================================================================

export interface HeroBaseProps {
  locale?: string;
  className?: string;
  messages?: Record<string, unknown>;
}

// =============================================================================
// 1. CINEMATIC HERO - Home Page
// =============================================================================

export interface CinematicHeroProps extends HeroBaseProps {
  /** Small text above title */
  eyebrow?: string;
  /** Main headline - supports JSX for styled spans */
  title?: React.ReactNode;
  /** Subtitle text */
  subtitle?: string;
  /** Video source URL */
  videoSrc?: string;
  /** Fallback/poster image URL */
  posterImage?: string;
  /** Scroll indicator text */
  scrollText?: string;
}

/**
 * Cinematic Hero - Full-screen video background
 * 
 * Concept One Home Page Hero (EXACT MATCH):
 * - Full viewport height (h-screen)
 * - Video background with opacity-70, scale-105
 * - Gradient overlay: from-stone-900/40 via-transparent to-stone-900/80
 * - Centered text with staggered fade-up animations
 * - Bouncing scroll indicator at bottom
 */
export function CinematicHero({
  eyebrow: eyebrowProp,
  title: titleProp,
  subtitle: subtitleProp,
  videoSrc = 'https://res.cloudinary.com/elementary-media/video/upload/f_auto,q_auto,vc_auto,w_1920/v1769763005/worlds_apart__one_standard_full_ick3gg.mp4',
  posterImage = 'https://res.cloudinary.com/elementary-media/video/upload/so_0,f_auto,q_auto,w_1920/v1769763005/worlds_apart__one_standard_full_ick3gg.jpg',
  scrollText: scrollTextProp,
  locale: _locale = 'en',
  messages,
  className = '',
}: CinematicHeroProps) {
  const hvMessages = ((messages?.stone as Record<string, unknown>)?.heroVariants as Record<string, unknown>)?.cinematic as Record<string, string> | undefined;
  const accessMessages = (messages?.stone as Record<string, unknown>)?.accessibility as Record<string, string> | undefined;
  const eyebrow = eyebrowProp ?? hvMessages?.eyebrow ?? 'Stone Investment';
  const title = titleProp ?? hvMessages?.title ?? 'The Art of Living';
  const subtitle = subtitleProp ?? hvMessages?.subtitle ?? 'Exceptional residences in Mauritius and Provence.';
  const scrollText = scrollTextProp ?? hvMessages?.scrollText ?? 'Discover';
  const heroAlt = accessMessages?.heroBackgroundAlt ?? 'Hero Background';
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVideoLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <header className={`relative w-full h-dvh overflow-hidden bg-stone-900 ${className}`} data-dark-hero="true">
      <div className={`absolute inset-0 transition-opacity duration-1000 ${isVideoLoaded ? 'opacity-100' : 'opacity-0'}`}>
        <video 
          autoPlay 
          muted 
          loop 
          playsInline 
          className="w-full h-full object-cover opacity-70 scale-105"
          poster={posterImage}
          onLoadedData={() => setIsVideoLoaded(true)}
        >
          <source src={videoSrc} type="video/mp4" />
          <Image 
            src={posterImage}
            alt={heroAlt}
            fill
            className="object-cover"
            priority
          />
        </video>
        <div className="absolute inset-0 bg-linear-to-b from-stone-900/40 via-transparent to-stone-900/80" />
      </div>

      <div className="relative z-10 h-full flex flex-col items-center justify-center text-center text-white px-6">
        <div className="overflow-hidden mb-8">
          <p 
            className="font-sans text-xs md:text-sm uppercase tracking-[0.4em] opacity-0 animate-fade-up"
            style={{ animationDelay: '0.2s' }}
          >
            {eyebrow}
          </p>
        </div>
        <h1 
          className="font-serif text-6xl md:text-9xl opacity-0 animate-fade-up leading-tight"
          style={{ animationDelay: '0.4s' }}
        >
          {title}
        </h1>
        <p 
          className="mt-8 font-sans font-light text-lg md:text-xl text-stone-200 max-w-xl opacity-0 animate-fade-up"
          style={{ animationDelay: '0.6s' }}
        >
          {subtitle}
        </p>

        <div 
          className="absolute bottom-12 opacity-0 animate-fade-up"
          style={{ animationDelay: '1s' }}
        >
          <div className="flex flex-col items-center gap-2 animate-bounce opacity-60">
            <span className="text-xs uppercase tracking-widest font-sans">{scrollText}</span>
            <ChevronDown size={20} />
          </div>
        </div>
      </div>
    </header>
  );
}

// =============================================================================
// 2. EDITORIAL HERO - The Firm Page
// =============================================================================

export interface EditorialHeroProps extends HeroBaseProps {
  /** Small text above title (e.g., "Est. 2008") */
  eyebrow?: string;
  /** Main headline */
  title?: string;
  /** Subtitle/description text */
  subtitle?: string;
  /** Background image URL */
  backgroundImage?: string;
  /** Whether to show bounce indicator */
  showScrollIndicator?: boolean;
}

/**
 * Editorial Hero - Full viewport with pulse animation background
 * 
 * Concept One TheFirm Page Hero (EXACT MATCH):
 * - Full dynamic viewport height (h-dvh)
 * - Background image with opacity-40 and pulse animation (10s)
 * - Dark overlay: bg-stone-900/50
 * - Centered text with fade-up animations
 * - Bouncing arrow at bottom
 */
export function EditorialHero({
  eyebrow: eyebrowProp,
  title: titleProp,
  subtitle: subtitleProp,
  backgroundImage = 'https://res.cloudinary.com/elementary-media/image/upload/v1772710942/damodash_luxury_real_estate_in_provence_france_editorial_8k_p_ee1096af-06e9-4e5c-a27f-d98428fdfa1c_2_nii047.png',
  showScrollIndicator = true,
  locale: _locale = 'en',
  messages,
  className = '',
}: EditorialHeroProps) {
  const hvMessages = ((messages?.stone as Record<string, unknown>)?.heroVariants as Record<string, unknown>)?.editorial as Record<string, string> | undefined;
  const accessMessages = (messages?.stone as Record<string, unknown>)?.accessibility as Record<string, string> | undefined;
  const eyebrow = eyebrowProp ?? hvMessages?.eyebrow ?? 'Est. 2008';
  const title = titleProp ?? hvMessages?.title ?? 'The Firm';
  const subtitle = subtitleProp ?? hvMessages?.subtitle ?? 'From the classical to the contemporary, we traverse centuries to provide a showcase for architectural heritage.';
  const bgAlt = accessMessages?.heroBackgroundAlt ?? 'Background';
  return (
    <header className={`relative w-full h-dvh flex flex-col items-center justify-center bg-stone-900 text-stone-50 overflow-hidden ${className}`} data-dark-hero="true">
      {/* Background Texture with Pulse Animation */}
      <div 
        className="absolute inset-0 opacity-40"
        style={{ animation: 'pulse 10s ease-in-out infinite' }}
      >
        <Image 
          src={backgroundImage}
          alt={bgAlt}
          fill
          className="object-cover scale-105"
          priority
        />
      </div>
      <div className="absolute inset-0 bg-stone-900/50" />
      
      <div className="relative z-10 text-center space-y-8 px-6 max-w-4xl">
        <p className="font-sans text-xs uppercase tracking-[0.4em] text-stone-400 animate-fade-up">
          {eyebrow}
        </p>
        <h1 
          className="font-serif text-6xl md:text-9xl text-stone-100 leading-none animate-fade-up"
          style={{ animationDelay: '0.1s' }}
        >
          {title}
        </h1>
        <div className="overflow-hidden">
          <p 
            className="font-sans font-light text-lg md:text-xl text-stone-300 max-w-2xl mx-auto leading-relaxed animate-fade-up"
            style={{ animationDelay: '0.2s' }}
          >
            {subtitle}
          </p>
        </div>
      </div>
      
      {showScrollIndicator && (
        <div className="absolute bottom-12 animate-bounce opacity-50 text-stone-400">
          <ArrowDown size={24} />
        </div>
      )}
    </header>
  );
}

// =============================================================================
// 3. ABSTRACT HERO - Valuations Page
// =============================================================================

export interface AbstractHeroProps extends HeroBaseProps {
  /** Small text above title */
  eyebrow?: string;
  /** Main headline - supports JSX for styled spans */
  title?: React.ReactNode;
  /** Subtitle/description text */
  subtitle?: string;
  /** Background image URL */
  backgroundImage?: string;
  /** Whether to show scroll indicator */
  showScrollIndicator?: boolean;
}

/**
 * Abstract Hero - 85vh with gradient fade to stone-50
 * 
 * Concept One Valuations Page Hero (EXACT MATCH):
 * - 85vh height with overflow-hidden
 * - Background image with opacity-50 and slower pulse (20s)
 * - Gradient: from-stone-900/60 via-stone-900/20 to-stone-50
 * - Content centered with fade-up animations
 * - ChevronDown scroll indicator
 */
export function AbstractHero({
  eyebrow: eyebrowProp,
  title: titleProp,
  subtitle: subtitleProp,
  backgroundImage = 'https://images.unsplash.com/photo-1464938050520-ef2270bb8ce8?q=80&w=2674&auto=format&fit=crop',
  showScrollIndicator = true,
  locale: _locale = 'en',
  messages,
  className = '',
}: AbstractHeroProps) {
  const hvMessages = ((messages?.stone as Record<string, unknown>)?.heroVariants as Record<string, unknown>)?.abstract as Record<string, string> | undefined;
  const accessMessages = (messages?.stone as Record<string, unknown>)?.accessibility as Record<string, string> | undefined;
  const eyebrow = eyebrowProp ?? hvMessages?.eyebrow ?? 'Strategic Advisory';
  const title = titleProp ?? hvMessages?.title ?? 'Precision & Value';
  const subtitle = subtitleProp ?? hvMessages?.subtitle ?? 'Unlocking the true potential of your asset through rigorous analysis and market intelligence.';
  const bgAlt = accessMessages?.heroBackgroundAlt ?? 'Abstract Background';
  return (
    <header className={`relative w-full h-[85vh] overflow-hidden bg-stone-900 ${className}`} data-dark-hero="true">
      <div 
        className="absolute inset-0 scale-105"
        style={{ animation: 'pulse 20s ease-in-out infinite' }}
      >
        <Image 
          src={backgroundImage}
          alt={bgAlt}
          fill
          className="object-cover opacity-50"
          priority
        />
        <div className="absolute inset-0 bg-linear-to-b from-stone-900/60 via-stone-900/20 to-stone-50" />
      </div>
      
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
        <div className="space-y-8 max-w-4xl">
          <p className="font-sans text-xs uppercase tracking-[0.4em] text-white/80 animate-fade-up">
            {eyebrow}
          </p>
          <h1 
            className="font-serif text-6xl md:text-8xl lg:text-9xl text-stone-100 leading-none animate-fade-up"
            style={{ animationDelay: '0.1s' }}
          >
            {title}
          </h1>
          <p 
            className="font-sans font-light text-lg md:text-xl text-stone-300 max-w-2xl mx-auto leading-relaxed animate-fade-up"
            style={{ animationDelay: '0.2s' }}
          >
            {subtitle}
          </p>
        </div>
      </div>

      {showScrollIndicator && (
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 animate-bounce opacity-50">
          <ChevronDown className="text-stone-400" size={24} />
        </div>
      )}
    </header>
  );
}

// =============================================================================
// CONVENIENCE EXPORTS
// =============================================================================

/**
 * Pre-configured hero for Home page (reads from stone.heroVariants.cinematic)
 */
export function HomeHero(props: Omit<CinematicHeroProps, 'title'>) {
  return (
    <CinematicHero
      {...props}
    />
  );
}

/**
 * Pre-configured hero for The Firm page (reads from stone.heroVariants.editorial)
 */
export function TheFirmHero(props: Omit<EditorialHeroProps, 'title' | 'eyebrow'>) {
  return (
    <EditorialHero
      {...props}
    />
  );
}

/**
 * Pre-configured hero for Valuations page (reads from stone.heroVariants.abstract)
 */
export function ValuationsHero(props: Omit<AbstractHeroProps, 'title' | 'eyebrow'>) {
  return (
    <AbstractHero
      {...props}
    />
  );
}

// =============================================================================
// 4. ARCHITECT HERO - Split Layout
// =============================================================================

export interface ArchitectHeroProps extends HeroBaseProps {
  /** Small text above title */
  eyebrow?: string;
  /** Main headline - supports JSX for styled spans */
  title?: React.ReactNode;
  /** Subtitle/description text */
  subtitle?: string;
  /** Background image URL for the right side */
  backgroundImage?: string;
  /** CTA button text */
  ctaText?: string;
  /** CTA button link */
  ctaLink?: string;
  /** Featured property card (optional floating card) */
  featuredCard?: {
    label?: string;
    title: string;
    subtitle: string;
  };
  /** Messages for i18n */
  messages?: Record<string, unknown>;
}

/**
 * Architect Hero - Split layout with content left, image right
 * 
 * Concept One Showcase "The Architect" Hero (EXACT MATCH):
 * - Full viewport height (h-screen)
 * - Left side (1/3): Content with title, subtitle, CTA
 * - Right side (2/3): Large background image with optional floating card
 * - Clean, architectural aesthetic with minimal animations
 */
export function ArchitectHero({
  eyebrow,
  title: titleProp,
  subtitle: subtitleProp,
  backgroundImage = 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=2670&auto=format&fit=crop',
  ctaText: ctaTextProp,
  ctaLink = '/properties',
  featuredCard,
  locale = 'en',
  messages,
  className = '',
}: ArchitectHeroProps) {
  const hvMessages = ((messages?.stone as Record<string, unknown>)?.heroVariants as Record<string, unknown>)?.architect as Record<string, string> | undefined;
  const accessMessages = (messages?.stone as Record<string, unknown>)?.accessibility as Record<string, string> | undefined;
  const title = titleProp ?? hvMessages?.title ?? 'Form & Function';
  const subtitle = subtitleProp ?? hvMessages?.subtitle ?? 'Curating spaces where architectural vision meets natural beauty. A portfolio defined by silence, light, and stone.';
  const ctaText = ctaTextProp ?? hvMessages?.ctaText ?? 'Discover Properties';
  const heroAlt = accessMessages?.heroBackgroundAlt ?? 'Hero Background';
  return (
    <div className={`w-full h-dvh flex flex-col md:flex-row bg-stone-50 ${className}`} data-dark-hero="false">
      {/* Left: Content */}
      <div className="w-full md:w-1/3 h-full flex flex-col justify-center p-8 md:p-12 lg:p-24 bg-stone-50 z-10">
        <div className="space-y-8 animate-fade-up">
          {eyebrow && (
            <p className="font-sans text-xs uppercase tracking-[0.4em] text-stone-400">
              {eyebrow}
            </p>
          )}
          <span className="w-12 h-1 bg-stone-900 block" />
          <h1 className="font-serif text-4xl md:text-5xl lg:text-7xl text-stone-900 leading-none">
            {title}
          </h1>
          <p className="font-sans text-stone-500 font-light leading-relaxed max-w-sm">
            {subtitle}
          </p>
          <div className="pt-4">
            <a 
              href={ctaLink.startsWith('/') && !ctaLink.startsWith(`/${locale}`) ? `/${locale}${ctaLink}` : ctaLink}
              className="group flex items-center gap-4 text-xs uppercase tracking-widest text-stone-900 hover:text-stone-500 transition-colors"
            >
              {ctaText}
              <svg 
                className="w-4 h-4 group-hover:translate-x-2 transition-transform" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </a>
          </div>
        </div>
      </div>

      {/* Right: Visual */}
      <div className="w-full md:w-2/3 h-full relative overflow-hidden">
        <Image 
          src={backgroundImage}
          alt={heroAlt}
          fill
          className="object-cover hover:scale-105 transition-transform duration-2000"
          priority
        />
        
        {/* Optional Floating Card */}
        {featuredCard && (
          <div className="absolute bottom-12 left-12 bg-white/90 backdrop-blur p-8 max-w-xs hidden md:block shadow-2xl">
            {featuredCard.label && (
              <p className="text-xs uppercase tracking-widest text-stone-400 mb-2">
                {featuredCard.label}
              </p>
            )}
            <h3 className="font-serif text-2xl text-stone-900">{featuredCard.title}</h3>
            <p className="mt-2 text-stone-500 font-sans text-xs">{featuredCard.subtitle}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// 5. ESSENCE HERO - Scroll-Driven Reveal Animation
// =============================================================================

export interface EssenceHeroProps extends HeroBaseProps {
  /** Small text above title */
  eyebrow?: string;
  /** Main headline text */
  title?: string;
  /** Background image URL */
  backgroundImage?: string;
  /** Scroll indicator text */
  scrollText?: string;
  /** Content to show after reveal */
  revealContent?: {
    title: string;
    description: string;
    ctaText?: string;
    ctaLink?: string;
  };
  /** Messages for i18n */
  messages?: Record<string, unknown>;
  /** Enable full interactive mode (default: true for standalone use) */
  interactive?: boolean;
}

/**
 * Essence Hero - Scroll-driven reveal animation
 * 
 * Concept One Showcase "The Essence" Hero (EXACT MATCH):
 * - Full viewport with scroll-based "door opening" animation
 * - Text scales and fades as the door opens
 * - Image zooms out as it reveals
 * - Includes post-reveal content section
 * 
 * This creates a psychological "threshold" effect where users
 * physically scroll to "push" the door open, creating a sense
 * of agency and arrival that auto-play cannot replicate.
 */
export function EssenceHero({
  eyebrow: eyebrowProp,
  title: titleProp,
  backgroundImage = 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?q=80&w=2574&auto=format&fit=crop',
  scrollText: scrollTextProp,
  revealContent: revealContentProp,
  locale = 'en',
  messages,
  className = '',
  interactive = true,
}: EssenceHeroProps) {
  const hvMessages = ((messages?.stone as Record<string, unknown>)?.heroVariants as Record<string, unknown>)?.essence as Record<string, string> | undefined;
  const eyebrow = eyebrowProp ?? hvMessages?.eyebrow ?? 'Stone Investment';
  const title = titleProp ?? hvMessages?.title ?? 'THE ESSENCE';
  const scrollText = scrollTextProp ?? hvMessages?.scrollText ?? 'Scroll to Enter';
  const revealContent = revealContentProp ?? {
    title: hvMessages?.revealTitle ?? 'A World Revealed',
    description: hvMessages?.revealDescription ?? 'The "Essence" concept relies on the psychological threshold of the door. By forcing the user to physically scroll to "push" the door open, we create a sense of agency and arrival that auto-play video cannot replicate.',
    ctaText: hvMessages?.revealCta ?? 'Explore Collection',
    ctaLink: '/properties',
  };
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [progress, setProgress] = useState(0);
  
  const targetProgressRef = useRef(0);
  const currentProgressRef = useRef(0);

  // Physics Loop for smooth animation
  useEffect(() => {
    let animationFrameId: number;
    
    const loop = () => {
      if (interactive) {
        // LERP: Heavier damping (0.05) for luxurious feel
        const diff = targetProgressRef.current - currentProgressRef.current;
        
        if (Math.abs(diff) > 0.0001) {
          currentProgressRef.current += diff * 0.05;
          setProgress(currentProgressRef.current);
        }
      } else {
        // AUTO PREVIEW LOOP (Sine Wave) for non-interactive mode
        const now = Date.now();
        const cycle = 6000;
        const t = (now % cycle);
        
        let target = 0;
        if (t < 2500) {
          const p = t / 2500;
          target = 1 - Math.pow(1 - p, 3);
        } else if (t < 3500) {
          target = 1;
        } else if (t < 5500) {
          const p = (t - 3500) / 2000;
          target = 1 - (p * p * p);
        }
        setProgress(target);
      }
      animationFrameId = requestAnimationFrame(loop);
    };

    animationFrameId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(animationFrameId);
  }, [interactive]);

  // Scroll Handler
  useEffect(() => {
    if (!interactive) return;

    const handleScroll = () => {
      if (scrollContainerRef.current) {
        const scrollTop = scrollContainerRef.current.scrollTop;
        const clientHeight = scrollContainerRef.current.clientHeight;
        const animationTrackHeight = clientHeight * 2.5;
        const rawProgress = Math.min(Math.max(scrollTop / animationTrackHeight, 0), 1);
        targetProgressRef.current = rawProgress;
      }
    };

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }
    return () => container?.removeEventListener('scroll', handleScroll);
  }, [interactive]);

  // Interpolation for door animation
  const minWidth = 20;
  const maxWidth = 100;
  const width = minWidth + (maxWidth - minWidth) * progress;
  
  const minHeight = 40;
  const maxHeight = 100;
  const height = minHeight + (maxHeight - minHeight) * progress;

  return (
    <div 
      ref={scrollContainerRef}
      className={`relative w-full ${interactive ? 'h-dvh overflow-y-auto' : 'h-dvh overflow-hidden'} bg-stone-100 no-scrollbar ${className}`}
      data-dark-hero="true"
    >
      {/* Scroll Track (Only in interactive mode) */}
      {interactive && (
        <div className="absolute top-0 left-0 w-full h-[350vh] pointer-events-none" />
      )}

      {/* Sticky Animation Container */}
      <div className={`sticky top-0 left-0 w-full h-dvh flex items-center justify-center overflow-hidden z-10`}>
        
        {/* THE DOOR (Mask) */}
        <div 
          className="relative z-10 overflow-hidden shadow-2xl will-change-transform"
          style={{
            width: `${width}%`,
            height: `${height}%`,
            maxWidth: '100vw',
            maxHeight: '100vh',
          }}
        >
          <div className="absolute inset-0 shadow-[inset_0_0_100px_rgba(0,0,0,0.5)] z-20 pointer-events-none" />
          
          {/* The "Fixed" Image behind the door */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-screen h-screen bg-stone-900">
            <Image 
              src={backgroundImage}
              alt="The Essence"
              fill
              className="object-cover will-change-transform"
              style={{
                transform: `scale(${1.15 - (progress * 0.15)})`
              }}
              priority
            />
            {/* Dynamic Contrast Overlay */}
            <div 
              className="absolute inset-0 bg-black/50 transition-opacity will-change-opacity pointer-events-none"
              style={{ opacity: Math.max(0, 0.6 - progress) }}
            />
          </div>
        </div>

        {/* THE TEXT (Foreground) */}
        <div 
          className="absolute z-20 pointer-events-none text-white flex flex-col items-center justify-center text-center will-change-transform"
          style={{
            transform: `scale(${1 + Math.pow(progress, 2) * 12})`, 
            opacity: Math.max(0, 1 - Math.max(0, progress - 0.15) * 2.5), 
            filter: `blur(${Math.max(0, (progress - 0.1) * 20)}px)`
          }}
        >
          <p className="font-sans text-[0.6rem] md:text-xs uppercase tracking-[0.5em] mb-4 drop-shadow-md">
            {eyebrow}
          </p>
          <h1 className="font-serif text-[12vw] md:text-[12vw] leading-none drop-shadow-[0_4px_24px_rgba(0,0,0,0.5)] overflow-hidden">
            {title}
          </h1>
        </div>

        {/* SCROLL INDICATOR */}
        <div 
          className="absolute bottom-12 left-1/2 -translate-x-1/2 z-20 text-stone-900 transition-opacity duration-300"
          style={{ opacity: interactive ? Math.max(0, 1 - progress * 4) : 0 }}
        >
          <div className="flex flex-col items-center gap-2 animate-bounce">
            <span className="text-xs uppercase tracking-widest font-sans">{scrollText}</span>
            <ChevronDown size={16} />
          </div>
        </div>
      </div>
      
      {/* Content Below the Fold (Only in interactive mode) */}
      {interactive && revealContent && (
        <div className="relative z-20 bg-white min-h-screen flex items-center justify-center mt-[250vh]">
          <div className="text-center space-y-8 p-12 max-w-2xl animate-fade-up">
            <span className="w-px h-16 bg-stone-300 mx-auto block" />
            <h2 className="font-serif text-4xl md:text-5xl text-stone-900">{revealContent.title}</h2>
            <p className="font-sans text-stone-500 leading-relaxed text-lg">
              {revealContent.description}
            </p>
            {revealContent.ctaText && revealContent.ctaLink && (
              <a 
                href={revealContent.ctaLink.startsWith('/') && !revealContent.ctaLink.startsWith(`/${locale}`) ? `/${locale}${revealContent.ctaLink}` : revealContent.ctaLink}
                className="inline-block px-8 py-4 bg-stone-900 text-white text-xs uppercase tracking-widest hover:bg-stone-700 transition-colors"
              >
                {revealContent.ctaText}
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Pre-configured hero for Properties page (reads from stone.heroVariants.properties)
 */
export function PropertiesHero(props: Omit<ArchitectHeroProps, 'title'>) {
  const hvMessages = ((props.messages?.stone as Record<string, unknown>)?.heroVariants as Record<string, unknown>)?.properties as Record<string, string> | undefined;
  return (
    <ArchitectHero
      {...props}
      title={hvMessages?.title ?? 'Our Collection'}
      subtitle={hvMessages?.subtitle ?? 'Discover exceptional properties, each carefully selected for its unique character and timeless beauty.'}
    />
  );
}

/**
 * Pre-configured hero for Showcase page (reads from stone.heroVariants.showcase)
 */
export function ShowcaseHero(props: Omit<EssenceHeroProps, 'title'>) {
  const hvMessages = ((props.messages?.stone as Record<string, unknown>)?.heroVariants as Record<string, unknown>)?.showcase as Record<string, string> | undefined;
  return (
    <EssenceHero
      {...props}
      title={hvMessages?.title ?? 'DISCOVER'}
      revealContent={{
        title: hvMessages?.revealTitle ?? 'Welcome to Stone Investment',
        description: hvMessages?.revealDescription ?? 'Experience luxury real estate reimagined. Our curated collection represents the finest properties in Mauritius and Provence.',
        ctaText: hvMessages?.revealCta ?? 'View Properties',
        ctaLink: '/properties',
      }}
    />
  );
}
